import { ThemeInput } from "src/types/common";

export const generateTheme = ({
  primary,
  secondary,
  primaryHover,
  secondaryHover,
  primaryBackground,
  secondaryBackground,
  background,
  foreground,
  border,
  card,
  chip,
  titleColor,
  subTitleColor,
  disabledTextColor,
}: ThemeInput) => {
  const primaryRGB = hexToRgb(primary);
  const secondaryRGB = hexToRgb(secondary);
  const hoverRGB = hexToRgb(primaryHover);

  // Card colors fall back to the app's original hardcoded Tailwind values
  // (bg-white / border-gray-200 / text-gray-900 / text-gray-500) so programs
  // that don't set these keep rendering exactly as before.
  const cardBackground = card?.background ?? "#ffffff";
  const cardBorder = card?.border ?? "#e5e7eb";
  const cardTitleColor = card?.titleColor ?? "#111827";
  const cardSubtitleColor = card?.subtitleColor ?? "#6b7280";
  const cardDescColor = card?.descColor ?? cardSubtitleColor;
  const resolvedTitleColor = titleColor ?? cardTitleColor;
  const resolvedSubTitleColor = subTitleColor ?? cardSubtitleColor;
  const resolvedDisabledTextColor = disabledTextColor ?? "#9ca3af";

  // Chip colors fall back to the app's original hardcoded Tailwind values
  // (bg-gray-200 / border-gray-300 / text-gray-500 inactive, app-primary
  // tinted active) so programs that don't set these render as before.
  const chipBackground = chip?.background ?? "#e5e7eb";
  const chipBorder = chip?.border ?? "#d1d5db";
  const chipColor = chip?.color ?? "#6b7280";
  const chipActiveBorder = chip?.activeBorder ?? primary;
  const chipActiveBackground = chip?.activeBackground ?? `rgba(${primaryRGB}, 0.1)`;
  const chipActiveColor = chip?.activeColor ?? primary;

  return {
    // Base colors
    "--background": background,
    "--foreground": foreground,
    "--card": background,
    "--card-foreground": foreground,

    // Card component colors (background/border/title/subtitle/description)
    "--card-background": cardBackground,
    "--card-border": cardBorder,
    "--card-title-color": cardTitleColor,
    "--card-subtitle-color": cardSubtitleColor,
    "--card-desc-color": cardDescColor,

    // Chip component colors (inactive/active background/border/text)
    "--chip-background": chipBackground,
    "--chip-border": chipBorder,
    "--chip-color": chipColor,
    "--chip-active-border": chipActiveBorder,
    "--chip-active-background": chipActiveBackground,
    "--chip-active-color": chipActiveColor,

    // Typography colors
    "--title-color": resolvedTitleColor,
    "--subtitle-color": resolvedSubTitleColor,
    "--disabled-text-color": resolvedDisabledTextColor,
    "--popover": background,
    "--popover-foreground": "#f8f8f8",
    "--primary": "#000000",
    "--primary-foreground": "#fafafa",
    "--secondary": "#f5f5f5",
    "--secondary-foreground": "#1a1a1a",
    "--muted": "#f5f5f5",
    "--muted-foreground": "#737373",
    "--accent": "#f5f5f5",
    "--accent-foreground": "#1a1a1a",
    "--destructive": "#ef4444",
    "--destructive-foreground": "#ffffff",
    "--border": border,
    "--input": border,
    "--ring": "#0a0a0a",
    "--chart-1": "222 94% 58%",
    "--chart-2": "215 79% 48%",
    "--chart-3": "217 33% 17%",
    "--chart-4": "208 79% 58%",
    "--chart-5": "210 92% 65%",
    "--radius": "0.5rem",

    // Sidebar
    "--sidebar-background": "#f8fafc",
    "--sidebar-foreground": "#334155",
    "--sidebar-primary": "#0f172a",
    "--sidebar-primary-foreground": "#ffffff",
    "--sidebar-accent": "#f1f5f9",
    "--sidebar-accent-foreground": "#0f172a",
    "--sidebar-border": "#e2e8f0",
    "--sidebar-ring": "#3b82f6",

    // App primary colors
    "--app-primary": primary,
    "--app-primary-hover": primaryHover,
    "--app-primary-rgb": primaryRGB,
    "--app-primary-light": lightenColor(primary, 0.75),
    "--app-primary-background": primaryBackground,

    // App secondary colors
    "--app-secondary": secondary,
    "--app-secondary-hover": secondaryHover,
    "--app-secondary-rgb": secondaryRGB,
    "--app-secondary-light": lightenColor(secondary, 0.75),
    "--app-secondary-background": secondaryBackground,

    "--card-header-text": "#000000",

    // Hero and UI and primary
    "--hero-bg-primary": lightenColor(primary, 0.1),
    "--hero-text-primary": foreground || "#1a202c",
    "--icon-bg-primary": `rgba(${primaryRGB}, 0.1)`,
    "--icon-bg-primary-light": `rgba(${primaryRGB}, 0.15)`,

    // Hero and UI and secondary
    "--hero-bg-secondary": lightenColor(secondary, 0.1),
    "--hero-text-secondary": foreground,
    "--icon-bg-secondary": `rgba(${secondaryRGB}, 0.1)`,
    "--icon-bg-secondary-light": `rgba(${secondaryRGB}, 0.15)`,

    // Banner
    "--banner-bg-start": `rgba(${primaryRGB}, 0.05)`,
    "--banner-bg-end": `rgba(${primaryRGB}, 0.1)`,
    "--banner-border": `rgba(${primaryRGB}, 0.2)`,

    // Chip
    "--chip-bg-primary": `rgba(${primaryRGB}, 0.1)`,
    "--chip-text-primary": primaryHover,
    "--chip-border-primary": `rgba(${primaryRGB}, 0.2)`,

    // AI
    "--ai-button-bg-start": primary,
    "--ai-button-bg-end": primaryHover,
    "--ai-button-hover-start": primaryHover,
    "--ai-button-hover-end": darkenColor(primaryHover, 0.2),
    "--ai-container-bg-start": `rgba(${primaryRGB}, 0.05)`,
    "--ai-container-bg-end": `rgba(${primaryRGB}, 0.1)`,
    "--ai-container-border": `rgba(${primaryRGB}, 0.2)`,
    "--ai-loading-border": primary,

    // Design Palette
    "--text-primary": "#000000", // foreground,
    "--text-secondary": "#64748b",
    "--text-muted": "#94a3b8",
    "--bg-primary": background,
    "--bg-secondary": `rgba(${primaryRGB}, 0.03)`,
    "--border-color": border,
    "--accent-primary": primary,
    "--accent-secondary": primaryHover,
    "--status-success": "#22c55e",
    "--status-warning": "#eab308",
    "--status-error": "#ef4444",
    "--status-info": "#3b82f6",
  };
};

// ----------- Utils ----------------

const hexToRgb = (hex: string): string => {
  const clean = hex.replace("#", "");
  const bigint = parseInt(clean, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `${r}, ${g}, ${b}`;
};

const lightenColor = (hex: string, percent: number): string => {
  const num = parseInt(hex.replace("#", ""), 16);
  const amt = Math.round(2.55 * percent * 100);
  const R = (num >> 16) + amt;
  const G = ((num >> 8) & 0x00ff) + amt;
  const B = (num & 0x0000ff) + amt;
  return (
    "#" +
    (
      0x1000000 +
      (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
      (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
      (B < 255 ? (B < 1 ? 0 : B) : 255)
    )
      .toString(16)
      .slice(1)
  );
};

const darkenColor = (hex: string, percent: number): string => {
  const num = parseInt(hex.replace("#", ""), 16);
  const amt = Math.round(2.55 * percent * 100);
  const R = (num >> 16) - amt;
  const G = ((num >> 8) & 0x00ff) - amt;
  const B = (num & 0x0000ff) - amt;
  return (
    "#" +
    (
      0x1000000 +
      (R > 0 ? R : 0) * 0x10000 +
      (G > 0 ? G : 0) * 0x100 +
      (B > 0 ? B : 0)
    )
      .toString(16)
      .slice(1)
  );
};
